import { useEffect, useRef, useState } from "react";
import "./AssetNameField.css";

interface AssetNameFieldProps {
  name: string;
  onSubmit: (name: string) => void;
  onCancel: () => void;
}

/**
 * Inline rename field, used by both the tile and the list row so renaming a
 * file looks the same wherever you start it. It reuses the folder rename
 * treatment — bare input, key-blue underline — because renaming a file and
 * renaming a folder should not be two different-looking acts.
 *
 * The extension is deliberately left out of the initial selection: typing
 * immediately replaces the stem and keeps `.png` intact, which is what people
 * mean ninety-nine times out of a hundred.
 */
export default function AssetNameField(props: AssetNameFieldProps) {
  const { name, onSubmit, onCancel } = props;
  const [value, setValue] = useState(name);
  const inputRef = useRef<HTMLInputElement>(null);
  // Escape blurs the field on its way out; without this the blur handler would
  // then save the very edit Escape just discarded.
  const cancelled = useRef(false);

  useEffect(() => {
    const input = inputRef.current;
    if (!input) return;

    input.focus();
    const dot = name.lastIndexOf(".");
    input.setSelectionRange(0, dot > 0 ? dot : name.length);
  }, [name]);

  const submit = () => {
    if (cancelled.current) return;
    onSubmit(value);
  };

  return (
    <input
      ref={inputRef}
      className="assetnamefield-input"
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onBlur={submit}
      // The row and the card both select on click; a click into the field is
      // not a click on the thing behind it.
      onClick={(e) => e.stopPropagation()}
      onDoubleClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (e.key === "Enter") {
          e.preventDefault();
          submit();
        } else if (e.key === "Escape") {
          e.preventDefault();
          cancelled.current = true;
          onCancel();
        }
      }}
      data-testid="asset-name-field"
    />
  );
}
